/**
 * Authenticated fetch wrapper. Adds the Cognito ID token to every request
 * as `Authorization: Bearer <jwt>`, parses JSON, throws on non-2xx with
 * structured error info.
 *
 * On 401 the caller decides what to do (typically AuthContext intercepts
 * and redirects to /signin).
 */

import { getConfig } from "./env";
import { loadTokens, tokensExpired } from "./auth";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly path: string,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface ApiRequestInit extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Skip auth header (for /api/health and similar public endpoints). */
  unauthenticated?: boolean;
  /**
   * Optional Cognito access token. When set, attaches it as
   * `x-cognito-access-token` for endpoints that need to call user-token
   * Cognito APIs server-side (currently only `/api/me/2fa/*`). Kept off
   * by default so the access token never leaves the SPA on unrelated
   * requests.
   */
  accessToken?: string;
}

/**
 * Call /api/<path>. Returns parsed JSON. Throws ApiError on non-2xx.
 */
export async function api<T = unknown>(
  path: string,
  init: ApiRequestInit = {},
): Promise<T> {
  const cfg = getConfig();
  const base = cfg.apiBaseUrl.replace(/\/$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  const url = `${base}${cleanPath}`;

  const headers = new Headers(init.headers);
  if (!headers.has("accept")) headers.set("accept", "application/json");

  if (!init.unauthenticated) {
    const tokens = loadTokens();
    if (tokens && !tokensExpired(tokens)) {
      headers.set("authorization", `Bearer ${tokens.idToken}`);
    }
  }

  if (init.accessToken) {
    headers.set("x-cognito-access-token", init.accessToken);
  }

  const {
    body: rawBody,
    unauthenticated: _unauth,
    headers: _h,
    accessToken: _at,
    ...rest
  } = init;

  const fetchInit: RequestInit = { ...rest, headers };
  if (rawBody !== undefined && rawBody !== null) {
    if (rawBody instanceof FormData) {
      fetchInit.body = rawBody;
    } else {
      headers.set("content-type", "application/json");
      fetchInit.body = JSON.stringify(rawBody);
    }
  }

  const res = await fetch(url, fetchInit);

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    // Prefer the API's human-readable `message` (e.g. "A brand with id
    // 'x' already exists.") over the machine `error` code ("conflict").
    // Fall back to the code, then a generic string.
    const obj = parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : null;
    const msg =
      obj && typeof obj.message === "string"
        ? obj.message
        : obj && typeof obj.error === "string"
          ? obj.error
          : `Request failed with ${res.status}`;
    throw new ApiError(res.status, cleanPath, parsed, msg);
  }

  return parsed as T;
}
